import { ARG_NAME_PATTERN, emptyArg, MAX_ARGS } from '../connectorToolConfig';

// Pasting a cURL: parsed into method, URL, headers and body, its token lifted out for a secret,
// and shown as a diff before anything in the form is replaced.

const NO_VALUE = new Set([
  '-s',
  '-S',
  '-L',
  '-k',
  '-i',
  '-v',
  '-f',
  '-g',
  '--silent',
  '--show-error',
  '--location',
  '--insecure',
  '--include',
  '--verbose',
  '--fail',
  '--compressed',
  '--globoff',
]);
const DATA = new Set(['-d', '--data', '--data-raw', '--data-binary', '--data-ascii', '--data-urlencode']);
// Options whose value has nothing to carry into the connector.
const SKIP_VALUE = new Set([
  '-o',
  '--output',
  '-A',
  '--user-agent',
  '-e',
  '--referer',
  '-m',
  '--max-time',
  '--connect-timeout',
  '--retry',
  '--max-redirs',
  '-x',
  '--proxy',
  '--cacert',
  '--cert',
  '-w',
  '--write-out',
  '-c',
  '--cookie-jar',
]);
// Options that carry a credential the connector cannot use: their value is read and left out.
const CREDENTIAL_VALUE = new Set(['-U', '--proxy-user', '--proxy-header', '--tlsuser', '--tlspassword', '--aws-sigv4']);

// A credential is a header or query name with one of these words in it (split on - _ . and camelCase),
// or a part containing one of the CREDENTIAL_IN words, so accesstoken or secretkey count too.
const SECRET_WORDS = new Set(['authorization', 'auth', 'signature', 'pass', 'pwd']);
const CREDENTIAL_IN = /token|secret|passw|credential|apikey|accesskey|appkey/;
// "key" and "sig" alone, or after one of these words, so sort_key or page_key stay ordinary.
const KEY_AFTER = new Set(['api', 'access', 'secret', 'private', 'client', 'subscription', 'app', 'auth', 'x']);
const isSecretName = (name) => {
  if (/idempotency/i.test(name)) return false;
  const parts = name
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[-_.]+/)
    .filter(Boolean);
  return parts.some(
    (part, i) =>
      SECRET_WORDS.has(part) ||
      CREDENTIAL_IN.test(part) ||
      ((part === 'key' || part === 'sig') && (parts.length === 1 || KEY_AFTER.has(parts[i - 1])))
  );
};

// Shell words: single and double quotes, $'…', backslash escapes and line continuations.
export const shellWords = (text) => {
  const src = String(text ?? '').replace(/\\\r?\n/g, ' ');
  const words = [];
  let word = null;
  let i = 0;
  const push = (chars) => {
    word = (word ?? '') + chars;
  };

  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) {
      if (word !== null) words.push(word);
      word = null;
      i += 1;
    } else if (ch === "'") {
      const end = src.indexOf("'", i + 1);
      if (end === -1) throw new Error('A quote is not closed.');
      push(src.slice(i + 1, end));
      i = end + 1;
    } else if (ch === '$' && src[i + 1] === "'") {
      let j = i + 2;
      let out = '';
      while (j < src.length && src[j] !== "'") {
        if (src[j] === '\\' && j + 1 < src.length) {
          const next = src[j + 1];
          out += { n: '\n', t: '\t', r: '\r' }[next] ?? next;
          j += 2;
        } else out += src[j++];
      }
      if (j >= src.length) throw new Error('A quote is not closed.');
      push(out);
      i = j + 1;
    } else if (ch === '"') {
      let j = i + 1;
      let out = '';
      while (j < src.length && src[j] !== '"') {
        if (src[j] === '\\' && '"\\$`'.includes(src[j + 1])) {
          out += src[j + 1];
          j += 2;
        } else out += src[j++];
      }
      if (j >= src.length) throw new Error('A quote is not closed.');
      push(out);
      i = j + 1;
    } else if (ch === '\\' && i + 1 < src.length) {
      push(src[i + 1]);
      i += 2;
    } else {
      push(ch);
      i += 1;
    }
  }
  if (word !== null) words.push(word);
  return words;
};

const hasShellVar = (value) => /\$\{?[A-Za-z_]/.test(value);
const SHELL_VAR_NOTE = 'Authorization (a shell variable, not its value)';
const toBase64 = (text) => window.btoa(String.fromCharCode(...new TextEncoder().encode(text)));

// The command as it reads: method, URL, headers and body. Throws with a reader-facing message.
export const parseCurl = (text) => {
  const words = shellWords(text);
  if (words[0] !== 'curl') throw new Error('Paste a command that starts with curl.');

  let method = null;
  let url = null;
  let get = false;
  let multipart = false;
  const headers = [];
  const data = [];
  const dropped = [];

  for (let i = 1; i < words.length; i += 1) {
    const word = words[i];
    const value = () => {
      i += 1;
      if (i >= words.length) throw new Error(`${word} needs a value.`);
      return words[i];
    };
    const attached = /^-([XHdbuFT])(.+)$/.exec(word);

    if (attached) {
      words.splice(i + 1, 0, attached[2]);
      words[i] = `-${attached[1]}`;
      i -= 1;
    } else if (word === '-X' || word === '--request') method = value().toUpperCase();
    else if (word === '-H' || word === '--header') {
      const line = value();
      const colon = line.indexOf(':');
      if (colon > 0) headers.push({ key: line.slice(0, colon).trim(), value: line.slice(colon + 1).trim() });
    } else if (DATA.has(word)) {
      const raw = value();
      if (raw.startsWith('@') && word !== '--data-raw') dropped.push('Data from a file');
      else if (word === '--data-urlencode') {
        const eq = raw.indexOf('=');
        data.push(
          eq === -1 ? encodeURIComponent(raw) : `${raw.slice(0, eq + 1)}${encodeURIComponent(raw.slice(eq + 1))}`
        );
      } else data.push(raw);
    } else if (word === '--json') {
      data.push(value());
      if (!headers.some((h) => /^content-type$/i.test(h.key)))
        headers.push({ key: 'Content-Type', value: 'application/json' });
    } else if (word === '-u' || word === '--user') {
      const user = value();
      if (hasShellVar(user)) dropped.push(SHELL_VAR_NOTE);
      else headers.push({ key: 'Authorization', value: `Basic ${toBase64(user)}` });
    } else if (word === '--oauth2-bearer') {
      headers.push({ key: 'Authorization', value: `Bearer ${value()}` });
    } else if (CREDENTIAL_VALUE.has(word)) {
      value();
      dropped.push(`${word} credentials`);
    } else if (word === '-b' || word === '--cookie') {
      value();
      dropped.push('Cookies');
    } else if (word === '-G' || word === '--get') get = true;
    else if (word === '-F' || word === '--form' || word === '--form-string') {
      value();
      multipart = true;
      dropped.push('Form data');
    } else if (word === '-T' || word === '--upload-file') {
      value();
      dropped.push('File upload');
    } else if (word === '--url') url = value();
    else if (SKIP_VALUE.has(word)) value();
    else if (NO_VALUE.has(word) || word.startsWith('-')) continue;
    // A bare word that looks like a URL wins over one taken earlier from an unknown option's value.
    else if (!url || (!/^https?:\/\//i.test(url) && /^https?:\/\//i.test(word))) url = word;
  }

  if (!url) throw new Error('No URL found in the command.');
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  // user:password@ in the URL is the same as -u.
  const userinfo = /^(https?:\/\/)([^/?#@]*)@/i.exec(url);
  if (userinfo) {
    url = url.replace(userinfo[0], userinfo[1]);
    let user = userinfo[2];
    try {
      user = decodeURIComponent(user);
    } catch {
      // Kept as written.
    }
    if (hasShellVar(user)) dropped.push(SHELL_VAR_NOTE);
    else headers.push({ key: 'Authorization', value: `Basic ${toBase64(user)}` });
  }

  let body = data.join('&');
  if (get && body) {
    url += (url.includes('?') ? '&' : '?') + body;
    body = '';
  }
  const finalMethod = get ? 'GET' : method || (body || multipart ? 'POST' : 'GET');
  if (finalMethod !== 'GET' && finalMethod !== 'POST')
    throw new Error(`Only GET and POST can be imported; this command is ${finalMethod}.`);
  if (finalMethod === 'GET' && body) dropped.push('Body left out of a GET');

  return { method: finalMethod, url, headers, body, dropped };
};

const splitUrl = (url) => {
  const noHash = url.split('#')[0];
  const at = noHash.indexOf('?');
  const base = at === -1 ? noHash : noHash.slice(0, at);
  const query = at === -1 ? '' : noHash.slice(at + 1);
  const params = query
    .split('&')
    .filter(Boolean)
    .map((pair) => {
      const eq = pair.indexOf('=');
      const key = eq === -1 ? pair : pair.slice(0, eq);
      const raw = eq === -1 ? '' : pair.slice(eq + 1);
      let decoded = raw;
      try {
        decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
      } catch {
        // Kept as written.
      }
      return { key, raw, value: decoded };
    });
  return { base: base.replace(/^http:\/\//i, 'https://'), params };
};

const argNameFor = (key) => {
  const name = key
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 31);
  return ARG_NAME_PATTERN.test(name) ? name : null;
};

// A connector has one header or query secret, so a credential in the body is blanked, never kept.
const blankBodySecrets = (body, dropped) => {
  const note = (key) => dropped.push(`${key} in the body (add it as a secret)`);
  try {
    const json = JSON.parse(body);
    if (json && typeof json === 'object') {
      let changed = false;
      const walk = (node) =>
        Object.keys(node).forEach((key) => {
          if (isSecretName(key) && Array.isArray(node[key])) {
            node[key] = node[key].map((item) => {
              if (item && typeof item === 'object') {
                walk(item);
                return item;
              }
              if (typeof item !== 'string' || item === '') return item;
              changed = true;
              note(key);
              return '';
            });
          } else if (isSecretName(key) && typeof node[key] !== 'object') {
            if (node[key] !== '') {
              node[key] = '';
              changed = true;
              note(key);
            }
          } else if (node[key] && typeof node[key] === 'object') walk(node[key]);
        });
      walk(json);
      return changed ? JSON.stringify(json, null, 2) : body;
    }
  } catch {
    // Not JSON: read as form fields below.
  }
  return body
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1 || eq === pair.length - 1 || !isSecretName(pair.slice(0, eq))) return pair;
      note(pair.slice(0, eq));
      return pair.slice(0, eq + 1);
    })
    .join('&');
};

// What the import would put in the form. Query parameters become arguments of a store tool (each
// with its value as the test value) unless listed in `literal`; the first token found is lifted out
// for a secret, and any further one is left out rather than kept in plain text.
export const curlToDraft = (parsed, { kind = 'tool', literal = [], declared = [] } = {}) => {
  const { base, params } = splitUrl(parsed.url);
  const dropped = [...parsed.dropped];
  let secret = null;
  const lift = (found, label) => {
    if (!found.value.trim()) dropped.push(`${label} (empty)`);
    else if (hasShellVar(found.value)) dropped.push(`${label} (a shell variable, not its value)`);
    else if (secret) dropped.push(`${label} (one secret per connector)`);
    else secret = found;
  };

  const headerRows = [];
  parsed.headers.forEach(({ key, value }) => {
    if (/^cookie$/i.test(key)) dropped.push('Cookies');
    else if (/authorization$/i.test(key)) {
      const scheme = /^(\S+)\s+(.+)$/.exec(value);
      lift(
        {
          value: scheme ? scheme[2] : value,
          authIn: 'header',
          authHeader: key,
          authPrefix: scheme ? `${scheme[1]} ` : '',
          authParam: '',
        },
        key
      );
    } else if (isSecretName(key))
      lift({ value, authIn: 'header', authHeader: key, authPrefix: '', authParam: '' }, key);
    else headerRows.push({ key, value });
  });

  const args = [];
  const taken = new Set(declared);
  const query = [];
  params.forEach((param) => {
    if (isSecretName(param.key)) {
      lift(
        { value: param.value, authIn: 'query', authHeader: 'Authorization', authPrefix: '', authParam: param.key },
        param.key
      );
      return;
    }
    const name = kind === 'tool' && !literal.includes(param.key) ? argNameFor(param.key) : null;
    if (name && (taken.has(name) || declared.length + args.length < MAX_ARGS)) {
      if (!taken.has(name)) args.push({ name, param: param.key, value: param.value });
      taken.add(name);
      query.push(`${param.key}={{args.${name}}}`);
    } else query.push(param.raw === '' && !param.value ? param.key : `${param.key}=${param.raw}`);
  });

  return {
    method: parsed.method,
    url: query.length ? `${base}?${query.join('&')}` : base,
    headerRows,
    body: parsed.method === 'POST' ? blankBodySecrets(parsed.body, dropped) : '',
    args,
    secret,
    dropped: [...new Set(dropped)],
    params: [...new Set(params.filter((param) => !isSecretName(param.key)).map((param) => param.key))],
  };
};

// The diff shown before Apply: + added, ~ changed, - removed, ↷ moved to a secret, × left out.
export const importDiff = (form, draft) => {
  const lines = [];
  const change = (label, before, after) => {
    if (before === after) return;
    if (!before) lines.push({ mark: '+', text: `${label} ${after}` });
    else if (!after) lines.push({ mark: '-', text: `${label} ${before}` });
    else lines.push({ mark: '~', text: `${label} ${before} → ${after}` });
  };

  change('Method', form.url.trim() ? form.method : '', draft.method);
  change('URL', form.url.trim(), draft.url);
  const before = Object.fromEntries(
    form.headerRows.filter((row) => row.key.trim()).map((row) => [row.key.trim(), row.value])
  );
  draft.headerRows.forEach((row) => change(`Header ${row.key}:`, before[row.key] ?? '', row.value));
  Object.keys(before)
    .filter((key) => !draft.headerRows.some((row) => row.key === key))
    .forEach((key) => change(`Header ${key}:`, before[key], ''));
  change('Body', form.method === 'POST' ? form.body.trim() : '', draft.body.trim());
  draft.args.forEach((arg) =>
    lines.push({ mark: '+', text: `Argument ${arg.name} (string, required) · test value ${arg.value || '(empty)'}` })
  );
  if (draft.secret && form.domainSecretId)
    lines.push({ mark: '~', text: 'Secret: the current secret is replaced by a new one' });
  if (draft.secret)
    lines.push({
      mark: '↷',
      text:
        draft.secret.authIn === 'query'
          ? `Query parameter ${draft.secret.authParam} moved to a new secret`
          : `${draft.secret.authHeader} moved to a new secret`,
    });
  draft.dropped.forEach((label) => lines.push({ mark: '×', text: `${label} left out` }));
  return lines;
};

// The form after Apply, and the test values the import brings. The secret's value is never in the
// form: the caller creates the secret first and passes its id.
export const applyImport = (form, draft, secretId = null) => {
  const rows = form.tool.argRows.filter((row) => row.name.trim());
  const declared = rows.map((row) => row.name.trim());
  const added = draft.args
    .filter((arg) => !declared.includes(arg.name))
    .map((arg) => ({ ...emptyArg(), name: arg.name, required: true }));
  const next = {
    ...form,
    method: draft.method,
    url: draft.url,
    headerRows: draft.headerRows,
    body: draft.body,
    tool: { ...form.tool, argRows: [...rows, ...added].slice(0, MAX_ARGS) },
  };
  if (draft.secret && secretId) {
    const { authIn, authHeader, authPrefix, authParam } = draft.secret;
    Object.assign(next, { domainSecretId: secretId, authIn, authHeader, authPrefix, authParam });
  }
  const testValues = Object.fromEntries(draft.args.map((arg) => [`args.${arg.name}`, arg.value]));
  return { form: next, testValues };
};
